
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from 'react-i18next';
import { Save } from 'lucide-react';
import { ContentEditor } from './editor/ContentEditor';
import { DateSelector } from './editor/DateSelector';
import { ImageUploader } from './editor/ImageUploader';
import { GithubLinkInput } from './editor/GithubLinkInput';

interface CardEditorProps {
  taskId: string;
  initialContent: string;
  startDate?: Date | null;
  dueDate?: Date | null;
  onSave: (newContent: string) => void;
}

export const CardEditor = ({ taskId, initialContent, startDate, dueDate, onSave }: CardEditorProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [content, setContent] = React.useState(initialContent);
  const [githubLink, setGithubLink] = React.useState('');
  const [selectedImage, setSelectedImage] = React.useState<File | null>(null);
  const [imagePreview, setImagePreview] = React.useState<string>('');
  const [newStartDate, setNewStartDate] = React.useState<Date | null>(startDate || null);
  const [newDueDate, setNewDueDate] = React.useState<Date | null>(dueDate || null);
  const [dateError, setDateError] = React.useState<string>('');
  const [isSaving, setIsSaving] = React.useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();

  // Update dates when props change
  React.useEffect(() => {
    setNewStartDate(startDate || null);
    setNewDueDate(dueDate || null);
  }, [startDate, dueDate]);

  const handleImageSelect = (file: File) => {
    setSelectedImage(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const validateDates = () => {
    if (newStartDate && newDueDate && newStartDate > newDueDate) {
      setDateError(t('errors.startDateAfterDueDate'));
      return false;
    }
    setDateError('');
    return true;
  };

  const handleSave = async () => {
    if (!validateDates()) return;

    try {
      setIsSaving(true);
      let finalContent = content;

      if (githubLink) {
        finalContent += `\n\nGitHub: ${githubLink}`;
      }

      if (selectedImage) {
        const fileExt = selectedImage.name.split('.').pop();
        const filePath = `${taskId}/${Math.random()}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from('task-attachments')
          .upload(filePath, selectedImage);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('task-attachments')
          .getPublicUrl(filePath);

        finalContent += `\n\n![Task attachment](${publicUrl})`;
      }

      const formattedStartDate = newStartDate 
        ? new Date(newStartDate.setHours(0, 0, 0, 0)).toISOString() 
        : null;
      const formattedDueDate = newDueDate 
        ? new Date(newDueDate.setHours(23, 59, 59, 999)).toISOString() 
        : null;

      const { error } = await supabase
        .from('tasks')
        .update({
          content: finalContent,
          start_date: formattedStartDate,
          due_date: formattedDueDate,
          updated_at: new Date().toISOString()
        })
        .eq('id', taskId);

      if (error) throw error;

      onSave(finalContent);
      setIsOpen(false);
      toast({
        title: t('common.success'),
        description: t('common.changesSaved'),
      });
    } catch (error) {
      console.error('Error saving card:', error);
      toast({
        title: t('common.error'),
        description: t('common.errorSavingChanges'),
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="w-full justify-start">
          {t('common.edit')}
        </Button>
      </DialogTrigger>
      <DialogContent 
        className="sm:max-w-[625px] overflow-y-auto max-h-[90vh] bg-background border shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>{t('common.editCard')}</DialogTitle>
          <DialogDescription>
            {t('common.editCardDescription')}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-8 py-4">
          <ContentEditor 
            content={content} 
            onChange={setContent} 
            onEnterPress={handleSave}
          />
          
          <div className="relative" style={{ zIndex: 99999 }}>
            <DateSelector
              startDate={newStartDate}
              dueDate={newDueDate}
              onStartDateChange={setNewStartDate}
              onDueDateChange={setNewDueDate}
              error={dateError}
            />
          </div>

          <div className="relative" style={{ zIndex: 1 }}>
            <GithubLinkInput value={githubLink} onChange={setGithubLink} />
          </div>
          
          <div className="relative" style={{ zIndex: 1 }}>
            <ImageUploader
              onImageSelect={handleImageSelect}
              imagePreview={imagePreview}
            />
          </div>

          <Button onClick={handleSave} disabled={isSaving} className="w-full">
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? t('common.saving') : t('common.save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
